import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CompactFilterBar, Input, Badge, Modal, Select, LoadingSpinner, Pagination } from '@/components/common';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '@/services/api';
import { ImportExportControls } from '@/components/admin/ImportExportControls';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/store';
import { canManageVehicleMaster } from './masterDataAccess';
import { BulkActionBar, BulkSelectAllCheckbox, BulkSelectCheckbox, useBulkSelection } from '@/components/bulk';
import type { PaginatedResponse } from '@/types';

interface VehicleMake {
  id: string;
  name: string;
  code: string;
  categories: string[];
  country_of_origin: string;
  is_active: boolean;
}

// `VehicleMake.categories` stores VehicleModel.CATEGORY_CHOICES -- the same six
// codes VehicleModelsPage already offers -- because that is what the GoDigit
// master import writes. This page offered an older four-code shorthand
// (2w/4w/cv/other) instead, so of 765 makes carrying a category, 689 held only
// codes with no checkbox: the Edit form opened with nothing ticked, and the
// list column fell through to printing the raw code, contradicting the note
// below it. Reported as "categories are not pre-selected"; the cause was that
// the two admin screens disagreed about the vocabulary.
//
// Never show the raw codes to the user.
const categoryOptions = [
  { value: '2w', label: 'Two Wheeler' },
  { value: '4w_private', label: 'Private Car' },
  { value: '4w_taxi', label: 'Taxi / Commercial Car' },
  { value: 'cv_gcv', label: 'Goods Carrying Vehicle' },
  { value: 'cv_pcv', label: 'Passenger Carrying Vehicle' },
  { value: 'cv_misc', label: 'Miscellaneous CV' },
  // Pre-import rows still carry these. They are offered so that editing such a
  // make does not silently drop a category it already has -- 8 makes on
  // production hold one -- but they are not what new work should use.
  { value: '4w', label: 'Four Wheeler (legacy)' },
  { value: 'cv', label: 'Commercial Vehicle (legacy)' },
  { value: 'other', label: 'Miscellaneous (legacy)' },
];

// The list filter's "All Categories" option. Uses the SAME raw codes as the
// create/edit form above (never the friendly-vs-raw split some other master
// data pages have) because `VehicleMakeViewSet.get_queryset` matches `?category=`
// against `VehicleMake.categories` directly for any value not in its
// long-form alias map -- these short codes already pass straight through.
const categoryFilterOptions = [{ value: '', label: 'All Categories' }, ...categoryOptions];

// Status is a plain is_active boolean -- '' means "don't filter", so the
// value that goes on the wire is only ever 'true' or 'false' once chosen.
const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const initialForm = { name: '', code: '', categories: [] as string[], country_of_origin: '' };

const itemsPerPage = 20;

function buildMakePayload(form: typeof initialForm) {
  return {
    name: form.name,
    code: form.code,
    country_of_origin: form.country_of_origin,
    categories: form.categories,
  };
}

export function VehicleMakesPage() {
  const queryClient = useQueryClient();
  // Read-only for everyone but the country admin -- which is exactly
  // where the server draws the line. See ./masterDataAccess.ts.
  const canManage = canManageVehicleMaster(useAuthStore().user?.role);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `search` drives the Input synchronously, `debouncedSearch` (300ms) is
  // what actually feeds the query below.
  const { term: search, debouncedTerm: debouncedSearch, setTerm: setSearch } = useDebouncedSearch();
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [formData, setFormData] = useState(initialForm);

  const { data, isLoading, isError, error, refetch } = useQuery<PaginatedResponse<VehicleMake>>({
    queryKey: ['vehicle-makes', page, debouncedSearch, filterCategory, filterStatus],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), page_size: String(itemsPerPage) });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filterCategory) params.set('category', filterCategory);
      if (filterStatus) params.set('is_active', filterStatus);
      const res = await api.get(`/vehicles/makes/?${params.toString()}`);
      return res.data;
    },
  });
  const makes = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const visibleIds = makes.map((m) => m.id);
  const selection = useBulkSelection(visibleIds);
  const activeFilterCount = [filterCategory, filterStatus].filter(Boolean).length;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['vehicle-makes'] });
    queryClient.invalidateQueries({ queryKey: ['vehicle-makes-lookup'] });
  };

  const createMutation = useMutation({
    mutationFn: async (data: typeof initialForm) => {
      if (editingId) {
        const res = await api.patch(`/vehicles/makes/${editingId}/`, buildMakePayload(data));
        return res.data;
      }
      const res = await api.post('/vehicles/makes/', buildMakePayload(data));
      return res.data;
    },
    onSuccess: () => {
      invalidate();
      toast.success(editingId ? 'Vehicle make updated' : 'Vehicle make created');
      setShowModal(false);
      setEditingId(null);
      setFormData(initialForm);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/vehicles/makes/${id}/`);
    },
    onSuccess: () => {
      invalidate();
      toast.success('Vehicle make deleted');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const handleEdit = (make: VehicleMake) => {
    setEditingId(make.id);
    setFormData({
      name: make.name,
      code: make.code,
      categories: make.categories || [],
      country_of_origin: make.country_of_origin || '',
    });
    setShowModal(true);
  };

  const toggleCategory = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      categories: prev.categories.includes(value)
        ? prev.categories.filter((c) => c !== value)
        : [...prev.categories, value],
    }));
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Delete this vehicle make? This cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(initialForm);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Vehicle Makes</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Manage vehicle manufacturers</p>
        </div>
        {canManage && (
        <div className="flex items-center gap-2">
          <ImportExportControls apiEndpoint="/vehicles/makes/" invalidateKeys={['vehicle-makes', 'vehicle-makes-lookup']} />
          <Button variant="primary" leftIcon={<PlusIcon className="w-4 h-4" />} onClick={() => setShowModal(true)}>
            Add Make
          </Button>
        </div>
        )}
      </div>

      <CompactFilterBar
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        searchPlaceholder="Search makes..."
        activeFilterCount={activeFilterCount}
        onClearFilters={() => {
          setFilterCategory('');
          setFilterStatus('');
          setPage(1);
        }}
      >
        <Select
          id="vehicle-make-filter-category"
          label="Category"
          options={categoryFilterOptions}
          value={filterCategory}
          onChange={(e) => {
            setFilterCategory(e.target.value);
            setPage(1);
          }}
        />
        <Select
          id="vehicle-make-filter-status"
          label="Status"
          options={statusOptions}
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(1);
          }}
        />
      </CompactFilterBar>

      {canManage && (
        <BulkActionBar
          resource="vehicles/makes"
          entityName="makes"
          selectedIds={selection.selectedIds}
          onChanged={invalidate}
          onClear={selection.clear}
        />
      )}

      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : isError ? (
          // A failed request is not "there are no vehicle makes" -- those are
          // two different states and this platform has a habit of confusing
          // them.
          <div role="alert" className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900">Could not load vehicle makes</h3>
            <p className="mt-2 text-gray-500">{getErrorMessage(error)}</p>
            <Button className="mt-4" variant="outline" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        ) : makes.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">No vehicle makes found</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Get started by adding a vehicle make.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    {canManage && (
                      <th className="px-6 py-3 text-left">
                        <BulkSelectAllCheckbox
                          checked={selection.allVisibleSelected}
                          indeterminate={selection.someVisibleSelected}
                          onChange={selection.toggleAllVisible}
                        />
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Country of Origin</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {makes.map((make) => (
                    <tr key={make.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      {canManage && (
                        <td className="px-6 py-4">
                          <BulkSelectCheckbox
                            checked={selection.isSelected(make.id)}
                            onChange={() => selection.toggle(make.id)}
                            label={`Select ${make.name}`}
                          />
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{make.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{make.code}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                        {(make.categories || [])
                          .map((c) => categoryOptions.find((o) => o.value === c)?.label || c)
                          .join(', ') || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{make.country_of_origin || '-'}</td>
                      <td className="px-6 py-4">
                        <Badge variant={make.is_active ? 'success' : 'warning'}>
                          {make.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          {canManage && (
                          <>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(make)}>
                            <PencilIcon className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(make.id)}>
                            <TrashIcon className="w-4 h-4 text-red-600" />
                          </Button>
                          </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Modal isOpen={showModal} onClose={handleCloseModal} title={editingId ? 'Edit Vehicle Make' : 'Add Vehicle Make'}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (formData.categories.length === 0) {
              toast.error('Select at least one category');
              return;
            }
            createMutation.mutate(formData);
          }}
          className="space-y-4"
        >
          <Input
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Code"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
            required
          />
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-red-500 ml-1">*</span>
            </label>
            {/* TRUE multi-select: a Make can span multiple categories (backend
                `VehicleMake.categories` JSONField), e.g. a manufacturer that
                makes both two-wheelers and four-wheelers. Friendly labels only
                (categoryOptions) — never the raw 2w/4w/cv/other codes. */}
            <div className="grid grid-cols-2 gap-2">
              {categoryOptions.map((o) => (
                <label key={o.value} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.categories.includes(o.value)}
                    onChange={() => toggleCategory(o.value)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{o.label}</span>
                </label>
              ))}
            </div>
          </div>
          <Input
            label="Country of Origin"
            value={formData.country_of_origin}
            onChange={(e) => setFormData({ ...formData, country_of_origin: e.target.value })}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
            <Button type="submit" isLoading={createMutation.isPending}>{editingId ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default VehicleMakesPage;
